import { DeserializedApplication } from '@amzn/awscat-portfolio-assessment-types';
import { ButtonDropdownProps, SelectProps } from '@amzn/awsui-components-react';
import { Data, Layout } from 'plotly.js-dist-min';

import { constants } from '../../../common/Localization';
import { deepCopy } from '../../../common/util';

export enum DEPENDENCY_STATUS {
    ACTIVE = 'active',
    INACTIVE = 'inactive',
    IN_REVIEW = 'in-review',
};

export enum STATUS_IN_GROUPING {
    ACTIVE = 'active',
    INACTIVE = 'inactive',
    IN_REVIEW = 'in-review',
};

export enum APP_SORT_OPTIONS {
    TOTAL = 'total',
    D_TOTAL = '-total',
    SERVER = 'server',
    D_SERVER = '-server',
    DATABASE = 'database',
    D_DATABASE = '-database',
};

export const MIN_Y_VALUE_WITHOUT_REPEAT_TICK = 4; // minimum Y value to avoid Plotly y legend repeat bug

export const appSortSelectOptions: SelectProps.Options = [
    {
        value: APP_SORT_OPTIONS.D_TOTAL,
        label: constants.SERVER_DB_DSC
    },
    {
        value: APP_SORT_OPTIONS.TOTAL,
        label: constants.SERVER_DB_ASC
    },
    {
        value: APP_SORT_OPTIONS.D_SERVER,
        label: constants.SERVER_DSC
    },
    {
        value: APP_SORT_OPTIONS.SERVER,
        label: constants.SERVER_ASC
    },
    {
        value: APP_SORT_OPTIONS.D_DATABASE,
        label: constants.DB_DSC
    },
    {
        value: APP_SORT_OPTIONS.DATABASE,
        label: constants.DB_ASC
    }
];

type MappingToUpdate = {
    applicationId: string;
    status: string;
};

export type DependencyDetail = {
    type: 'application' | 'server' | 'database';
    hidden: boolean;
    resourceId: string;
    applications: Set<string>;
    servers: Set<string>;
    databases: Set<string>;
};

export const INFRASTRUCTURE_GROUP_ID = 'shared-infrastructure';

export const isInfrastructureGroup = (id: string): boolean => id === INFRASTRUCTURE_GROUP_ID;

export type EnhancedApplication = DeserializedApplication & { groupName: string; };

export type AppServerMappingToUpdate = MappingToUpdate & {
    serverId: string;
};

export type AppDatabaseMappingToUpdate = MappingToUpdate & {
    databaseId: string;
};

export const GROUPING_ACTIONS: ButtonDropdownProps.Items = [
    { id: 'inferDependencies', text: constants.START_FROM_THE_BEGINNING },
    { id: 'markAsSharedInfra', text: constants.MARK_AS_SHARED_INFRASTRUCTURE },
    { id: 'removeByType', text: constants.REMOVE_DEPENDENCIES_BY_TYPE },
    { id: 'removeApplications', text: constants.REMOVE_APPLICATONS },
    { id: 'inReview', text: constants.IN_REVIEW_DEPENDENCIES },
    { id: 'recreateGroups', text: constants.RECREATE_GROUPS_DROPDOWN_OPTION },
];

export const generateHeatMapData = (board: number[][], order: {frequency: string[]; name: string[]}): Partial<Data>[] => [
    {
        z: deepCopy(board),
        x: order.frequency,
        y: order.frequency,
        type: 'heatmap',
        opacity: 0.5,
        showscale: true,
        hoverinfo: 'text',
        colorscale: [
            [0, 'rgb(230, 230, 230)'],
            [0.01, 'rgb(230, 230, 230)'],

            [0.01, 'rgb(230, 230, 255)'],
            [0.1, 'rgb(230, 230, 255)'],

            [0.1, 'rgb(204, 204, 255)'],
            [0.2, 'rgb(204, 204, 255)'],

            [0.2, 'rgb(179, 179, 255)'],
            [0.3, 'rgb(179, 179, 255)'],

            [0.3, 'rgb(153, 153, 255)'],
            [0.4, 'rgb(153, 153, 255)'],

            [0.4, 'rgb(128, 128, 255)'],
            [0.5, 'rgb(128, 128, 255)'],

            [0.5, 'rgb(102, 102, 255)'],
            [0.6, 'rgb(102, 102, 255)'],

            [0.6, 'rgb(77, 77, 255)'],
            [0.7, 'rgb(77, 77, 255)'],

            [0.7, 'rgb(51, 51, 255)'],
            [0.8, 'rgb(51, 51, 255)'],

            [0.8, 'rgb(26, 26, 255)'],
            [0.9, 'rgb(26, 26, 255)'],

            [0.9, 'rgb(0, 0, 255)'],
            [1.0, 'rgb(0, 0, 255)'],
        ],
        xgap: 1,
        ygap: 1
    },
    {
        z: [],
        zmin: 0,
        zmax: 1,
        type: 'heatmap',
        showscale: false,
        colorscale: [
            [0, 'rgba(0, 0, 0, 0)'],
            [1, 'rgb(247, 191, 156)']
        ],
        hoverinfo: 'none',
        xgap: 1,
        ygap: 1
    }
];

export const generateHeatMapLayout = (length: number): Partial<Layout> => {
    return {
        width: length * Math.min(Math.max(800 / length, 10), 100) + 160,
        height: length * Math.min(Math.max(800 / length, 10), 100) + 180,
        xaxis: {
            ticks: '',
            showgrid: false,
            side: 'top',
            tickmode: 'array',
            tickfont: {
                size: Math.min(Math.max(600 / length, 8), 12),
            }
        },
        yaxis: {
            ticks: '',
            showgrid: false,
            ticksuffix: ' ',
            tickmode: 'array',
            tickfont: {
                size: Math.min(Math.max(600 / length, 8), 12),
            },
            autorange: 'reversed'
        },
        autosize: true,
    };
};

export const DEFAULT_WAVE_CAPACITY = {
    applications: 20,
    servers: 150,
    databases: 30
};
